import { useState } from "react";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  DollarSignIcon,
  XIcon,
} from "lucide-react";

type SubmitForApprovalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  period: {
    id: string;
    label: string;
    startDate: string;
    endDate: string;
    totalEmployees: number;
    employeesComplete: number;
  };
  onConfirm: (notes: string) => void | Promise<void>;
  totalPayroll?: number;
};

export function SubmitForApprovalModal({
  isOpen,
  onClose,
  period,
  onConfirm,
  totalPayroll = 487250,
}: SubmitForApprovalModalProps) {
  const [notes, setNotes] = useState("");

  if (!isOpen) {
    return null;
  }

  const incompleteCount = period.totalEmployees - period.employeesComplete;
  const employeeCount = period.totalEmployees;

  const handleSubmit = async () => {
    await onConfirm(notes);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-iron-grey/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-[12px] bg-surface-light shadow-lg animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border p-6">
          <div>
            <h2 className="font-title text-xl font-semibold text-iron-grey">
              Submit for Approval
            </h2>
            <p className="mt-1 font-body text-sm text-text-secondary">
              {period.label}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-[6px] p-2 text-text-secondary transition-colors hover:bg-muted hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-200px)] space-y-6 overflow-y-auto p-6">
          {incompleteCount > 0 ? (
            <div className="flex items-start gap-3 rounded-r-[8px] border-l-4 border-coral bg-warning-background/50 p-4">
              <AlertCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-coral" />
              <div>
                <h3 className="font-body text-sm font-semibold text-iron-grey">
                  {incompleteCount} employee{incompleteCount !== 1 ? "s" : ""} incomplete
                </h3>
                <p className="mt-1 font-body text-sm text-text-secondary">
                  Some employees have missing time entries or adjustments. You
                  can still submit, but review may be delayed.
                </p>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-[8px] bg-muted/50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span className="font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Employees
                </span>
              </div>
              <div className="font-title text-2xl font-semibold text-iron-grey">
                {employeeCount}
              </div>
              <div className="mt-1 font-body text-xs text-text-secondary">
                {period.employeesComplete} complete
              </div>
            </div>

            <div className="rounded-[8px] bg-muted/50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-jungle" />
                <span className="font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Total Payroll
                </span>
              </div>
              <div className="font-title text-2xl font-semibold text-iron-grey">
                ${totalPayroll.toLocaleString()}
              </div>
              <div className="mt-1 font-body text-xs text-text-secondary">
                Gross amount
              </div>
            </div>

            <div className="rounded-[8px] bg-muted/50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-aquamarine" />
                <span className="font-body text-xs font-medium uppercase tracking-wider text-text-secondary">
                  Period
                </span>
              </div>
              <div className="font-body text-sm font-semibold text-iron-grey">
                {new Date(period.startDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}{" "}
                -{" "}
                {new Date(period.endDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </div>
              <div className="mt-1 font-body text-xs text-text-secondary">
                14 days
              </div>
            </div>
          </div>

          <div className="rounded-[8px] border border-border bg-surface-light p-5">
            <h3 className="mb-4 font-body text-sm font-semibold text-iron-grey">
              Gross Payroll Breakdown
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-text-secondary">
                  Base Salary Total
                </span>
                <span className="font-body text-sm font-medium text-iron-grey">
                  ${(totalPayroll * 0.92).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-text-secondary">
                  Stipends & Adjustments
                </span>
                <span className="font-body text-sm font-medium text-iron-grey">
                  ${(totalPayroll * 0.08).toLocaleString()}
                </span>
              </div>
              <div className="my-2 h-px bg-border" />
              <div className="flex items-center justify-between pt-1">
                <span className="font-body text-sm font-bold text-iron-grey">
                  Gross Total
                </span>
                <span className="font-body text-lg font-bold text-primary">
                  ${totalPayroll.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-[8px] border border-border bg-surface-light p-5">
            <div className="mb-4 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-jungle" />
              <h3 className="font-body text-sm font-semibold text-iron-grey">
                Submission Notes
              </h3>
            </div>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={4}
              placeholder="Add optional notes for the approver..."
              data-testid="payroll-submit-notes"
              className="w-full resize-none rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="rounded-[8px] border border-border bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <DollarSignIcon className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <h3 className="font-body text-sm font-semibold text-iron-grey">
                  Approval Workflow
                </h3>
                <p className="mt-1 font-body text-sm text-text-secondary">
                  Submission locks the preparer lane and sends this pay period
                  to approvers for review. Unlock remains available after export
                  if corrections are required.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSubmit()}
            data-testid="payroll-submit-confirm"
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover"
          >
            Submit for Approval
          </button>
        </div>
      </div>
    </div>
  );
}
